import { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import { Slot, useRouter, useSegments } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import * as Updates from "expo-updates";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ClubProvider, useClub } from "@/contexts/ClubContext";
import { DevProvider } from "@/contexts/DevContext";
import { getItem } from "@/lib/storage";
import { trackActivity } from "@/lib/activity";
import StripeWrapper from "@/components/StripeWrapper";
import AnimatedSplash from "@/components/AnimatedSplash";
import { KeyboardProvider } from "react-native-keyboard-controller";
import React from "react";
import { View, Text, ScrollView } from "react-native";

class DebugErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null; info: any }
> {
  state = { error: null as Error | null, info: null as any };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: any) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
    this.setState({ info });
  }
  render() {
    if (this.state.error) {
      return (
        <ScrollView style={{ flex: 1, backgroundColor: "#fff", padding: 20, paddingTop: 60 }}>
          <Text style={{ fontSize: 18, fontWeight: "800", color: "#c00", marginBottom: 12 }}>
            App crashed
          </Text>
          <Text selectable style={{ fontSize: 14, color: "#000", marginBottom: 12 }}>
            {this.state.error.message}
          </Text>
          <Text selectable style={{ fontSize: 11, color: "#444", fontFamily: "Courier" }}>
            {this.state.error.stack}
          </Text>
          {this.state.info?.componentStack && (
            <Text selectable style={{ fontSize: 11, color: "#666", marginTop: 12, fontFamily: "Courier" }}>
              {this.state.info.componentStack}
            </Text>
          )}
        </ScrollView>
      );
    }
    return this.props.children;
  }
}
export { DebugErrorBoundary as ErrorBoundary };

SplashScreen.preventAutoHideAsync();

function NavigationGate() {
  const { user, loading: authLoading } = useAuth();
  const { membership, clubIds, loading: clubLoading } = useClub();
  const segments = useSegments();
  const router = useRouter();
  const isAuthLink = typeof window !== "undefined" && window.location?.pathname?.startsWith("/auth/");
  const [showSplash, setShowSplash] = useState(!isAuthLink);
  const [hasSeenWelcome, setHasSeenWelcome] = useState<boolean | null>(null);
  const hasTrackedLaunch = useRef(false);

  const loading = authLoading || clubLoading || hasSeenWelcome === null;

  console.log("[NavGate]", {
    authLoading, clubLoading, hasSeenWelcome,
    user: user?.uid ?? null,
    clubIds: clubIds.length,
    membership: membership?.id ?? null,
    segments: segments.join("/"),
    loading,
  });

  // Track app launch once when user is available
  useEffect(() => {
    if (user && !hasTrackedLaunch.current) {
      hasTrackedLaunch.current = true;
      trackActivity("app_launch", user.uid, user.email);
    }
  }, [user]);

  // Check first-launch flag
  useEffect(() => {
    getItem("hasSeenWelcome").then((val) => {
      setHasSeenWelcome(val === "true");
    });
  }, []);

  useEffect(() => {
    if (loading) return;

    const inAuth = segments[0] === "(auth)";
    const inAuthToken = segments[0] === "auth";
    const inOnboarding = segments[0] === "(onboarding)";
    const inApp = segments[0] === "(app)";

    // On the /auth/[token] screen: don't redirect until sign-in + club data is fully loaded
    if (inAuthToken) {
      // Not signed in yet — verification still in progress
      if (!user) return;
      // Signed in but club data still loading — wait for it
      if (clubLoading) return;
      // Data is ready — fall through to normal routing below
    }

    if (!user) {
      if (!inAuth) {
        if (!hasSeenWelcome && Platform.OS !== "web") {
          router.replace("/(auth)/welcome");
        } else {
          router.replace("/(auth)/login");
        }
      }
    } else if (clubIds.length === 0) {
      if (!inOnboarding) router.replace("/(onboarding)");
    } else if (!membership) {
      if (!inOnboarding) router.replace("/(onboarding)/pick-club");
    } else {
      if (!inApp) router.replace("/(app)");
    }
  }, [user, membership, clubIds, loading, segments, hasSeenWelcome]);

  // Is the user on the correct route group?
  const isSettled = !loading && (() => {
    const seg = segments[0];
    if (seg === "auth") return true;
    if (!user) return seg === "(auth)";
    if (clubIds.length === 0) return seg === "(onboarding)";
    if (!membership) return seg === "(onboarding)";
    return seg === "(app)";
  })();

  return (
    <>
      <Slot />
      {/* Cover stale content while loading or waiting for redirect to land */}
      {!showSplash && !isSettled && (
        <View style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "#fff" }} />
      )}
      {showSplash && (
        <AnimatedSplash
          onReady={() => SplashScreen.hideAsync()}
          ready={isSettled}
          onFinish={() => setShowSplash(false)}
        />
      )}
    </>
  );
}

function AppProviders({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return (
    <ClubProvider>
      <DevProvider userEmail={user?.email} userId={user?.uid}>
        <StripeWrapper>{children}</StripeWrapper>
      </DevProvider>
    </ClubProvider>
  );
}

export default function RootLayout() {
  const fontsLoaded = true; // SVG icons used everywhere, no font loading needed

  // Check for OTA updates on launch (native only)
  useEffect(() => {
    if (Platform.OS === "web") return;
    async function checkForUpdate() {
      try {
        const update = await Updates.checkForUpdateAsync();
        if (update.isAvailable) {
          await Updates.fetchUpdateAsync();
          await Updates.reloadAsync();
        }
      } catch (e) {
        // Silently fail — don't block the app
      }
    }
    checkForUpdate();
  }, []);

  if (!fontsLoaded) return null;

  try {
    return (
      <KeyboardProvider>
        <SafeAreaProvider>
          <AuthProvider>
            <AppProviders>
              <NavigationGate />
            </AppProviders>
          </AuthProvider>
        </SafeAreaProvider>
      </KeyboardProvider>
    );
  } catch (e) {
    console.error("Root layout crash:", e);
    return null;
  }
}
